import React, { useState, useEffect, useRef } from 'react';
import { 
  AlertCircle, Check, Building, Users, Shield, AlertTriangle, Plus, X, Save, 
  Edit, Trash2, Eye, FileText, Camera, Upload, Download, Share2, Calendar,
  DollarSign, Settings, Search, Filter, ChevronDown, ChevronUp, MapPin,
  Phone, Mail, User, Bike, Wrench, Gas, Timer, Star, Image
} from 'lucide-react';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  onSnapshot,
  setDoc 
} from 'firebase/firestore';
import { db } from '../firebase';

// Document Viewer Component
const DocumentViewer = ({ document, onClose }) => {
  if (!document) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-white">{document.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="text-center">
          {document.type === 'image' ? (
            <img 
              src={document.url} 
              alt={document.name}
              className="max-w-full max-h-96 mx-auto rounded-lg"
            />
          ) : (
            <div className="bg-gray-700 p-8 rounded-lg">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-300">Document preview not available</p>
              <a 
                href={document.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download size={16} className="mr-2" />
                Download Document
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Document Type Manager Component
const DocumentTypeManager = ({ show, onClose, documentTypes, setDocumentTypes }) => {
  const [newType, setNewType] = useState('');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState('');

  if (!show) return null;

  const handleAddType = () => {
    if (newType.trim() && !documentTypes.includes(newType.trim())) {
      setDocumentTypes([...documentTypes, newType.trim()]);
      setNewType('');
    }
  };

  const handleEditType = (index) => {
    setEditingIndex(index);
    setEditingValue(documentTypes[index]);
  };

  const handleSaveEdit = () => {
    if (editingValue.trim() && !documentTypes.includes(editingValue.trim())) {
      const updated = [...documentTypes];
      updated[editingIndex] = editingValue.trim();
      setDocumentTypes(updated);
    }
    setEditingIndex(-1);
    setEditingValue('');
  };

  const handleDeleteType = (index) => {
    if (documentTypes[index] !== 'Other') {
      setDocumentTypes(documentTypes.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-white">Manage Document Types</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              placeholder="New document type"
              className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              onKeyPress={(e) => e.key === 'Enter' && handleAddType()}
            />
            <button
              onClick={handleAddType}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Add
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {documentTypes.map((type, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-gray-700 rounded">
                {editingIndex === index ? (
                  <>
                    <input
                      type="text"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      className="flex-1 px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white"
                    />
                    <button onClick={handleSaveEdit} className="text-green-400">
                      <Check size={16} />
                    </button>
                    <button onClick={() => setEditingIndex(-1)} className="text-gray-400">
                      <X size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-white">{type}</span>
                    <button onClick={() => handleEditType(index)} className="text-blue-400">
                      <Edit size={16} />
                    </button>
                    {type !== 'Other' && (
                      <button onClick={() => handleDeleteType(index)} className="text-red-400">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Bike Details Form Component
const BikeDetailsForm = ({ 
  editingBikeId, 
  bikes, 
  setBikes, 
  setSaveStatus, 
  documentTypes, 
  setDocumentTypes,
  onSave,
  setShowDocumentTypeManager,
  setViewingDocument,
  saveManagedBike,
  currentUser,
  currentCompany,
  companyId
}) => {
  // Image upload refs
  const mainImageInputRef = useRef(null);
  const additionalImagesInputRef = useRef(null);

  const [formData, setFormData] = useState({
    regNumber: '',
    model: '',
    year: '',
    color: '',
    dailyRate: '',
    description: '',
    status: 'available',
    purchaseDate: '',
    purchasePrice: '',
    insuranceExpiry: '',
    maintenanceNotes: '',
    fuelType: 'petrol',
    engineSize: '',
    mileage: '',
    condition: 'excellent',
    location: '',
    vin: '',
    engineNumber: '',
    documents: [],
    mainImage: '',
    additionalImages: []
  });

  const [loading, setLoading] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);

  useEffect(() => {
    if (editingBikeId) {
      const bikeToEdit = bikes.find(bike => bike.id === editingBikeId);
      if (bikeToEdit) {
        setFormData({
          ...bikeToEdit,
          mainImage: bikeToEdit.mainImage || '',
          additionalImages: bikeToEdit.additionalImages || []
        });
      }
    }
  }, [editingBikeId, bikes]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Image handling functions
  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, mainImage: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    
    if ((formData.additionalImages?.length || 0) + files.length > 5) {
      alert('You can only add up to 5 additional images');
      return;
    }

    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert('All images should be less than 5MB');
      return;
    }

    const readFiles = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(event.target.result);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readFiles).then((images) => {
      setFormData(prev => ({
        ...prev,
        additionalImages: [...(prev.additionalImages || []), ...images]
      }));
    });
  };

  const removeMainImage = () => {
    setFormData(prev => ({ ...prev, mainImage: '' }));
    if (mainImageInputRef.current) {
      mainImageInputRef.current.value = '';
    }
  };

  const removeAdditionalImage = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalImages: (prev.additionalImages || []).filter((_, i) => i !== index)
    }));
  };

  const handleDocumentAdd = () => {
    const newDoc = {
      id: Date.now(),
      name: 'New Document',
      type: 'Other',
      url: '',
      uploadedAt: new Date().toISOString(),
      uploadedBy: currentUser?.username || 'Unknown'
    };
    setFormData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), newDoc]
    }));
  };

  const handleDocumentUpdate = (docId, field, value) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.map(doc => 
        doc.id === docId ? { ...doc, [field]: value } : doc
      )
    }));
  };

  const handleDocumentDelete = (docId) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(doc => doc.id !== docId)
    }));
  };

  const validateForm = () => {
    if (!formData.regNumber?.trim()) {
      alert('Registration number is required');
      return false;
    }
    if (!formData.model?.trim()) {
      alert('Model is required');
      return false;
    }
    if (!formData.dailyRate || isNaN(parseFloat(formData.dailyRate))) {
      alert('Valid daily rate is required');
      return false;
    }

    // Check for duplicate registration number
    const regNumberUpper = formData.regNumber.trim().toUpperCase();
    const existingBike = bikes.find(bike => 
      bike.regNumber?.toUpperCase() === regNumberUpper && 
      bike.id !== editingBikeId
    );

    if (existingBike) {
      alert(`A bike with registration number "${formData.regNumber}" already exists`);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setSaveStatus('saving');

    try {
      const bikeData = {
        ...formData,
        regNumber: formData.regNumber.trim().toUpperCase(),
        model: formData.model.trim(),
        dailyRate: parseFloat(formData.dailyRate),
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : null,
        engineSize: formData.engineSize ? parseFloat(formData.engineSize) : null,
        mileage: formData.mileage ? parseFloat(formData.mileage) : null,
        updatedAt: new Date(),
        updatedBy: currentUser?.id || 'unknown',
        updatedByUsername: currentUser?.username || 'Unknown User',
        mainImage: formData.mainImage || '',
        additionalImages: formData.additionalImages || []
      };

      if (!editingBikeId) {
        bikeData.createdAt = new Date();
        bikeData.createdBy = currentUser?.id || 'unknown';
        bikeData.createdByUsername = currentUser?.username || 'Unknown User';
      }

      console.log(`🚗 ${editingBikeId ? 'Updating' : 'Adding'} bike:`, bikeData.regNumber);
      
      await saveManagedBike(bikeData, !!editingBikeId, editingBikeId);

      // Dispatch event to update pricing across the app
      if (editingBikeId) {
        window.dispatchEvent(new CustomEvent('bikeUpdated', {
          detail: { 
            action: 'updated',
            companyId: companyId,
            bikeId: editingBikeId,
            regNumber: bikeData.regNumber,
            model: bikeData.model,
            dailyRate: bikeData.dailyRate,
            timestamp: new Date().toISOString()
          }
        }));
        console.log('🔄 Bike updated event dispatched');
      } else {
        window.dispatchEvent(new CustomEvent('bikeAdded', {
          detail: { 
            action: 'added',
            companyId: companyId,
            regNumber: bikeData.regNumber,
            model: bikeData.model,
            dailyRate: bikeData.dailyRate,
            timestamp: new Date().toISOString()
          }
        }));
        console.log('🚀 Bike added event dispatched');
      }

      alert(`✅ Bike ${editingBikeId ? 'updated' : 'added'} successfully!`);
      
      // Reset form
      setFormData({
        regNumber: '',
        model: '',
        year: '',
        color: '',
        dailyRate: '',
        description: '',
        status: 'available',
        purchaseDate: '',
        purchasePrice: '',
        insuranceExpiry: '',
        maintenanceNotes: '',
        fuelType: 'petrol',
        engineSize: '',
        mileage: '',
        condition: 'excellent',
        location: '',
        vin: '',
        engineNumber: '',
        documents: [],
        mainImage: '',
        additionalImages: []
      });
      
      if (onSave) onSave();

    } catch (error) {
      console.error('Error saving bike:', error);
      alert('❌ Error saving bike: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">
          {editingBikeId ? 'Edit Bike' : 'Add New Bike'}
        </h3>
        <button
          onClick={() => onSave && onSave()}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      <div className="space-y-6">
        {/* Bike Images Section */}
        <div className="bg-gray-600 rounded-lg p-5 border border-gray-500">
          <h4 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Camera size={20} className="text-orange-400" />
            Bike Images
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Main Image */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Main Image
              </label>
              <input
                type="file"
                ref={mainImageInputRef}
                onChange={handleMainImageChange}
                className="hidden"
                accept="image/*"
                disabled={loading}
              />
              
              {formData.mainImage ? (
                <div className="relative">
                  <img
                    src={formData.mainImage}
                    alt="Main bike"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    onClick={removeMainImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors"
                    disabled={loading}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => mainImageInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-gray-500 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-orange-400 hover:bg-gray-700/50 transition-colors"
                  disabled={loading}
                >
                  <Image size={32} className="text-gray-400" />
                  <span className="text-gray-400 text-sm">Click to upload main image</span>
                  <span className="text-gray-500 text-xs">Max 5MB</span>
                </button>
              )}
            </div>

            {/* Additional Images */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Additional Images ({formData.additionalImages?.length || 0}/5)
              </label>
              <input
                type="file"
                ref={additionalImagesInputRef}
                onChange={handleAdditionalImagesChange}
                className="hidden"
                accept="image/*"
                multiple
                disabled={loading}
              />
              
              <div className="space-y-2">
                {formData.additionalImages && formData.additionalImages.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {formData.additionalImages.map((img, index) => (
                      <div key={index} className="relative">
                        <img
                          src={img}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeAdditionalImage(index)}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full shadow-lg transition-colors"
                          disabled={loading}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                
                {(!formData.additionalImages || formData.additionalImages.length < 5) && (
                  <button
                    onClick={() => additionalImagesInputRef.current?.click()}
                    className="w-full py-3 border-2 border-dashed border-gray-500 rounded-lg flex items-center justify-center gap-2 hover:border-orange-400 hover:bg-gray-700/50 transition-colors"
                    disabled={loading}
                  >
                    <Plus size={16} className="text-gray-400" />
                    <span className="text-gray-400 text-sm">Add more images</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div>
          <h4 className="text-lg font-medium text-white mb-4">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Registration Number *
              </label>
              <input
                type="text"
                value={formData.regNumber}
                onChange={(e) => handleInputChange('regNumber', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="e.g., BHV 9643"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Model *
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="e.g., DIO, APRILIA"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Daily Rate (USD) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.dailyRate}
                onChange={(e) => handleInputChange('dailyRate', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="25.00"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Year</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="2023"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="Red"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                disabled={loading}
              >
                <option value="available">Available</option>
                <option value="rented">Rented</option>
                <option value="maintenance">Under Maintenance</option>
                <option value="retired">Retired</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Condition</label>
              <select
                value={formData.condition}
                onChange={(e) => handleInputChange('condition', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                disabled={loading}
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
          </div>
        </div>

        {/* Technical Details */}
        <div>
          <h4 className="text-lg font-medium text-white mb-4">Technical Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Fuel Type</label>
              <select
                value={formData.fuelType}
                onChange={(e) => handleInputChange('fuelType', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                disabled={loading}
              >
                <option value="petrol">Petrol</option>
                <option value="electric">Electric</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Engine Size (cc)</label>
              <input
                type="number"
                value={formData.engineSize}
                onChange={(e) => handleInputChange('engineSize', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="150"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Mileage (km)</label>
              <input
                type="number"
                value={formData.mileage}
                onChange={(e) => handleInputChange('mileage', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="15000"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">VIN Number</label>
              <input
                type="text"
                value={formData.vin}
                onChange={(e) => handleInputChange('vin', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="Vehicle Identification Number"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Engine Number</label>
              <input
                type="text"
                value={formData.engineNumber}
                onChange={(e) => handleInputChange('engineNumber', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="Engine Serial Number"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div>
          <h4 className="text-lg font-medium text-white mb-4">Financial Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Purchase Date</label>
              <input
                type="date"
                value={formData.purchaseDate}
                onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Purchase Price (USD)</label>
              <input
                type="number"
                step="0.01"
                value={formData.purchasePrice}
                onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="5000.00"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Insurance Expiry</label>
              <input
                type="date"
                value={formData.insuranceExpiry}
                onChange={(e) => handleInputChange('insuranceExpiry', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Description and Location */}
        <div>
          <h4 className="text-lg font-medium text-white mb-4">Additional Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="Main Garage"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                placeholder="Additional notes about this bike..."
                disabled={loading}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Maintenance Notes</label>
            <textarea
              value={formData.maintenanceNotes}
              onChange={(e) => handleInputChange('maintenanceNotes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
              placeholder="Record maintenance history, upcoming services, known issues..."
              disabled={loading}
            />
          </div>
        </div>

        {/* Documents Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-white">Documents</h4>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDocumentTypeManager(true)}
                className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-500"
                disabled={loading}
              >
                <Settings size={14} className="inline mr-1" />
                Manage Types
              </button>
              <button
                onClick={() => setShowDocuments(!showDocuments)}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                disabled={loading}
              >
                {showDocuments ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {showDocuments ? 'Hide' : 'Show'} Documents
              </button>
            </div>
          </div>

          {showDocuments && (
            <div className="space-y-3">
              {(formData.documents || []).map((doc) => (
                <div key={doc.id} className="bg-gray-600 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Document Name</label>
                      <input
                        type="text"
                        value={doc.name}
                        onChange={(e) => handleDocumentUpdate(doc.id, 'name', e.target.value)}
                        className="w-full px-2 py-1 bg-gray-700 border border-gray-500 rounded text-white text-sm"
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Document Type</label>
                      <select
                        value={doc.type}
                        onChange={(e) => handleDocumentUpdate(doc.id, 'type', e.target.value)}
                        className="w-full px-2 py-1 bg-gray-700 border border-gray-500 rounded text-white text-sm"
                        disabled={loading}
                      >
                        {documentTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-300 mb-1">Document URL</label>
                        <input
                          type="url"
                          value={doc.url}
                          onChange={(e) => handleDocumentUpdate(doc.id, 'url', e.target.value)}
                          className="w-full px-2 py-1 bg-gray-700 border border-gray-500 rounded text-white text-sm"
                          placeholder="https://..."
                          disabled={loading}
                        />
                      </div>
                      <button
                        onClick={() => handleDocumentDelete(doc.id)}
                        className="p-1 text-red-400 hover:text-red-300"
                        disabled={loading}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                onClick={handleDocumentAdd}
                className="w-full py-2 border-2 border-dashed border-gray-500 rounded-lg text-gray-400 hover:border-gray-400 hover:text-gray-300 transition-colors"
                disabled={loading}
              >
                <Plus size={16} className="inline mr-2" />
                Add Document
              </button>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-600">
          <button
            onClick={() => onSave && onSave()}
            disabled={loading}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-colors flex items-center space-x-2"
          >
            <Save size={16} />
            <span>{loading ? 'Saving...' : (editingBikeId ? 'Update Bike' : 'Add Bike')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Bike List Component
const BikeList = ({ 
  bikes, 
  selectedModel, 
  setSelectedModel, 
  onEditBike, 
  onDeleteBike, 
  setViewingDocument,
  sharingStatus,
  setSharingStatus,
  showShareOptions,
  setShowShareOptions,
  currentUser,
  currentCompany,
  companyId
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('regNumber');
  const [sortOrder, setSortOrder] = useState('asc');

  const models = [...new Set(bikes.map(bike => bike.model))].sort();

  const filteredBikes = bikes
    .filter(bike => {
      const matchesSearch = 
        bike.regNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bike.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bike.color?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bike.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || bike.status === statusFilter;
      const matchesModel = !selectedModel || bike.model === selectedModel;

      return matchesSearch && matchesStatus && matchesModel;
    })
    .sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      
      if (sortBy === 'dailyRate' || sortBy === 'purchasePrice' || sortBy === 'mileage') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'rented': return 'bg-blue-100 text-blue-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'retired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'excellent': return 'text-green-400';
      case 'good': return 'text-blue-400';
      case 'fair': return 'text-yellow-400';
      case 'poor': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const handleShare = (bike) => {
    const bikeInfo = `Bike Details:\n\nReg: ${bike.regNumber}\nModel: ${bike.model}\nYear: ${bike.year || 'N/A'}\nColor: ${bike.color || 'N/A'}\nDaily Rate: $${bike.dailyRate}\nStatus: ${bike.status}\nCondition: ${bike.condition}\n\nManaged by ${currentCompany?.name || 'Bike Manager'}`;
    
    navigator.clipboard.writeText(bikeInfo).then(() => {
      setSharingStatus(prev => ({ ...prev, [bike.id]: 'success' }));
      setTimeout(() => {
        setSharingStatus(prev => ({ ...prev, [bike.id]: null }));
      }, 3000);
    }).catch(() => {
      alert('Failed to copy bike details');
    });
  };

  if (bikes.length === 0) {
    return (
      <div className="text-center py-12">
        <Bike className="mx-auto h-20 w-20 text-gray-400 mb-6" />
        <h3 className="text-2xl font-semibold text-gray-300 mb-4">No Bikes Found</h3>
        <p className="text-gray-500 text-lg">Start by adding your first bike to the fleet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">Search Bikes</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by registration, model, color..."
                className="w-full pl-10 pr-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Status Filter</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="rented">Rented</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
            >
              <option value="regNumber">Registration</option>
              <option value="model">Model</option>
              <option value="year">Year</option>
              <option value="dailyRate">Daily Rate</option>
              <option value="status">Status</option>
              <option value="condition">Condition</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Order</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>

        {models.length > 0 && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedModel(null)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  !selectedModel 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                }`}
              >
                All Models ({bikes.length})
              </button>
              {models.map(model => {
                const count = bikes.filter(bike => bike.model === model).length;
                return (
                  <button
                    key={model}
                    onClick={() => setSelectedModel(selectedModel === model ? null : model)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedModel === model 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                    }`}
                  >
                    {model} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        <p className="text-gray-400">
          Showing {filteredBikes.length} of {bikes.length} bikes
          {selectedModel && ` (${selectedModel} model)`}
        </p>
        {selectedModel && (
          <button
            onClick={() => setSelectedModel(null)}
            className="text-orange-400 hover:text-orange-300 flex items-center gap-1"
          >
            <X size={16} />
            Clear filter
          </button>
        )}
      </div>

      {/* Bikes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBikes.map(bike => (
          <div key={bike.id} className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden hover:border-orange-500 transition-colors">
            {/* Bike Image */}
            {bike.mainImage && (
              <div className="w-full h-48 overflow-hidden bg-gray-800">
                <img
                  src={bike.mainImage}
                  alt={bike.regNumber}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            {/* Bike Header */}
            <div className="p-4 border-b border-gray-600">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold text-white">{bike.regNumber}</h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(bike.status)}`}>
                  {bike.status}
                </span>
              </div>
              <p className="text-gray-300">{bike.model} {bike.year && `(${bike.year})`}</p>
              {bike.color && (
                <p className="text-gray-400 text-sm">Color: {bike.color}</p>
              )}
            </div>

            {/* Bike Details */}
            <div className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Daily Rate:</span>
                  <span className="text-orange-400 font-semibold">${bike.dailyRate}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Condition:</span>
                  <span className={`font-medium ${getConditionColor(bike.condition)}`}>
                    {bike.condition}
                  </span>
                </div>

                {bike.mileage && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Mileage:</span>
                    <span className="text-gray-300">{bike.mileage.toLocaleString()} km</span>
                  </div>
                )}

                {bike.fuelType && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Fuel:</span>
                    <span className="text-gray-300 capitalize">{bike.fuelType}</span>
                  </div>
                )}

                {bike.location && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Location:</span>
                    <span className="text-gray-300">{bike.location}</span>
                  </div>
                )}
              </div>

              {bike.description && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <p className="text-gray-400 text-sm">{bike.description}</p>
                </div>
              )}

              {bike.documents && bike.documents.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <p className="text-gray-400 text-xs mb-2">Documents ({bike.documents.length})</p>
                  <div className="flex flex-wrap gap-1">
                    {bike.documents.slice(0, 3).map(doc => (
                      <button
                        key={doc.id}
                        onClick={() => setViewingDocument(doc)}
                        className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                      >
                        {doc.type}
                      </button>
                    ))}
                    {bike.documents.length > 3 && (
                      <span className="text-xs text-gray-400 px-2 py-1">
                        +{bike.documents.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Images Indicator */}
              {bike.additionalImages && bike.additionalImages.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Camera size={14} />
                    <span>{bike.additionalImages.length} more {bike.additionalImages.length === 1 ? 'photo' : 'photos'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-gray-600 bg-gray-800">
              <div className="flex justify-between items-center">
                <div className="flex space-x-2">
                  <button
                    onClick={() => onEditBike(bike)}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-600 rounded transition-colors"
                    title="Edit Bike"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleShare(bike)}
                    className={`p-2 rounded transition-colors ${
                      sharingStatus[bike.id] === 'success'
                        ? 'text-green-400 bg-green-900'
                        : 'text-gray-400 hover:text-gray-300 hover:bg-gray-600'
                    }`}
                    title="Share Bike Details"
                  >
                    {sharingStatus[bike.id] === 'success' ? <Check size={16} /> : <Share2 size={16} />}
                  </button>
                </div>
                
                <button
                  onClick={() => onDeleteBike(bike.id)}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-600 rounded transition-colors"
                  title="Delete Bike"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredBikes.length === 0 && (
        <div className="text-center py-12">
          <Search className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No bikes found</h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all' || selectedModel
              ? 'Try adjusting your search or filters'
              : 'No bikes match your criteria'
            }
          </p>
        </div>
      )}
    </div>
  );
};

// Main Bike Management Component
const BikeManagement = ({
  currentUser,
  currentCompany, 
  getCompanyContext,
  hasPermission
}) => {
  const [bikes, setBikes] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBikeId, setEditingBikeId] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([
    'Registration',
    'Insurance',
    'Revenue License',
    'Emission Test',
    'Service Record',
    'Other'
  ]);
  const [showDocumentTypeManager, setShowDocumentTypeManager] = useState(false);
  
  const [sharingStatus, setSharingStatus] = useState({});
  const [showShareOptions, setShowShareOptions] = useState(null);

  const companyId = getCompanyContext();
  const managedBikesCollection = collection(db, 'managedBikes');
  const documentTypesCollection = collection(db, 'documentTypes');

  const [sessionId] = useState(() => {
    const id = Math.random().toString(36).substring(2) + Date.now().toString(36);
    console.log(`🔑 Session ID: ${id}`);
    return id;
  });

  useEffect(() => {
    if (!hasPermission('bikeManagement')) {
      setError('You do not have permission to access Bike Management');
      setLoading(false);
      return;
    }

    if (!companyId) {
      setError('No company context available');
      setLoading(false);
      return;
    }

    initializeCompanyData();
  }, [companyId, hasPermission]);

  const initializeCompanyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🚀 BikeManagement: Initializing data for company ${companyId}`);
      
      await Promise.all([
        loadCompanyDocumentTypes(),
        loadCompanyManagedBikes()
      ]);
      
    } catch (error) {
      console.error('BikeManagement: Error initializing company data:', error);
      setError('Failed to initialize bike management: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyDocumentTypes = async () => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    try {
      console.log(`Loading document types for company: ${companyId}`);
      const q = query(documentTypesCollection, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const types = [];
        querySnapshot.forEach((doc) => {
          types.push(doc.data().name);
        });
        
        if (!types.includes('Other')) {
          types.push('Other');
        }
        
        setDocumentTypes(types);
        console.log(`Loaded ${types.length} document types for company ${companyId}`);
      } else {
        await initializeCompanyDocumentTypes();
      }
    } catch (error) {
      console.error('Error loading company document types:', error);
    }
  };

  const initializeCompanyDocumentTypes = async () => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    try {
      console.log(`Initializing default document types for company: ${companyId}`);
      const defaultTypes = [
        'Registration',
        'Insurance', 
        'Revenue License',
        'Emission Test',
        'Service Record',
        'Other'
      ];

      const addPromises = defaultTypes.map(type => 
        addDoc(documentTypesCollection, { 
          name: type,
          companyId: companyId,
          companyName: currentCompany?.name || 'Unknown Company',
          createdAt: new Date(),
          createdBy: currentUser?.id || 'system',
          createdByUsername: currentUser?.username || 'System'
        })
      );
      await Promise.all(addPromises);
      
      setDocumentTypes(defaultTypes);
      console.log(`Initialized ${defaultTypes.length} document types for company ${companyId}`);
    } catch (error) {
      console.error('Error initializing company document types:', error);
    }
  };

  const saveCompanyDocumentTypes = async (types) => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    try {
      console.log(`Saving document types for company: ${companyId}`);
      
      const q = query(documentTypesCollection, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      const addPromises = types.map(type => 
        addDoc(documentTypesCollection, { 
          name: type,
          companyId: companyId,
          companyName: currentCompany?.name || 'Unknown Company',
          createdAt: new Date(),
          createdBy: currentUser?.id || 'system',
          createdByUsername: currentUser?.username || 'System'
        })
      );
      await Promise.all(addPromises);
      
      console.log(`Saved ${types.length} document types for company ${companyId}`);
    } catch (error) {
      console.error('Error saving company document types:', error);
    }
  };

  const loadCompanyManagedBikes = async () => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    try {
      console.log(`Loading managed bikes for company: ${companyId}`);
      const q = query(managedBikesCollection, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      const loadedBikes = [];
      
      querySnapshot.forEach((doc) => {
        loadedBikes.push({ id: doc.id, ...doc.data() });
      });
      
      console.log(`Loaded ${loadedBikes.length} managed bikes for company ${companyId}`);
      setBikes(loadedBikes);
      
    } catch (error) {
      console.error('Error loading company managed bikes:', error);
      throw error;
    }
  };

  const saveManagedBike = async (bikeData, isEdit = false, docId = null) => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    try {
      setSaveStatus('saving');
      
      const bikeDataWithCompany = {
        ...bikeData,
        companyId: companyId,
        companyName: currentCompany?.name || 'Unknown Company',
        updatedAt: new Date(),
        updatedBy: currentUser?.id || 'unknown',
        updatedByUsername: currentUser?.username || 'Unknown User',
        sessionId: sessionId
      };
      
      if (isEdit && docId) {
        console.log(`🔄 Updating bike ${docId} in Firestore`);
        const bikeRef = doc(db, 'managedBikes', docId);
        await updateDoc(bikeRef, bikeDataWithCompany);
        console.log(`✅ Bike ${docId} updated in Firestore`);
      } else {
        const newBikeData = {
          ...bikeDataWithCompany,
          createdAt: new Date(),
          createdBy: currentUser?.id || 'unknown',
          createdByUsername: currentUser?.username || 'Unknown User'
        };
        
        console.log(`🆕 Adding new bike to Firestore:`, newBikeData.regNumber);
        const docRef = await addDoc(managedBikesCollection, newBikeData);
        console.log(`✅ New bike added to Firestore with ID:`, docRef.id);
      }
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
      
    } catch (error) {
      console.error('Error saving company managed bike:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
      throw error;
    }
  };

  const deleteManagedBike = async (bikeId) => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    try {
      setSaveStatus('saving');
      
      const bikeToDelete = bikes.find(bike => bike.id === bikeId);
      if (!bikeToDelete) {
        throw new Error('Bike not found');
      }
      
      if (bikeToDelete.companyId !== companyId) {
        throw new Error('You can only delete bikes from your own company');
      }
      
      console.log(`🗑️ Deleting bike ${bikeId} from Firestore`);
      
      await deleteDoc(doc(db, 'managedBikes', bikeId));
      console.log(`✅ Bike ${bikeId} deleted from Firestore`);
      
      window.dispatchEvent(new CustomEvent('bikeDeleted', {
        detail: { 
          action: 'deleted',
          companyId: companyId,
          bikeId: bikeId,
          regNumber: bikeToDelete.regNumber,
          model: bikeToDelete.model,
          timestamp: new Date().toISOString()
        }
      }));
      console.log('🗑️ Bike deleted event dispatched');
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
      
    } catch (error) {
      console.error('Error deleting company managed bike:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
      throw error;
    }
  };

  useEffect(() => {
    if (!companyId) {
      console.warn('No company ID for managed bikes listener');
      return;
    }

    console.log(`📡 Setting up managed bikes listener for company: ${companyId} (Session: ${sessionId})`);
    
    const q = query(managedBikesCollection, where('companyId', '==', companyId));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log(`🔄 Real-time listener triggered, docs count:`, querySnapshot.docs.length);
      
      const updatedBikes = [];
      querySnapshot.forEach((doc) => {
        updatedBikes.push({ id: doc.id, ...doc.data() });
      });
      
      setBikes(updatedBikes);
      console.log(`✅ Real-time update: ${updatedBikes.length} managed bikes for company ${companyId}`);
    }, (error) => {
      console.error('Error in managed bikes subscription:', error);
    });

    return () => {
      console.log(`📡 Unsubscribing managed bikes listener for company: ${companyId}`);
      unsubscribe();
    };
  }, [companyId, sessionId]);

  useEffect(() => {
    if (documentTypes.length > 0 && companyId) {
      saveCompanyDocumentTypes(documentTypes);
    }
  }, [documentTypes, companyId]);

  useEffect(() => {
    const timeouts = Object.keys(sharingStatus).map(bikeId => {
      if (sharingStatus[bikeId] === 'success') {
        return setTimeout(() => {
          setSharingStatus(prev => ({ ...prev, [bikeId]: null }));
        }, 3000);
      }
      return null;
    }).filter(Boolean);
    
    return () => timeouts.forEach(timeout => clearTimeout(timeout));
  }, [sharingStatus]);

  const handleEditBike = (bike) => {
    if (bike.companyId !== companyId) {
      alert('You can only edit bikes from your own company');
      return;
    }
    
    setEditingBikeId(bike.id);
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBikeSaved = () => {
    setShowAddForm(false);
    setEditingBikeId(null);
  };

  const handleDeleteBike = async (bikeId) => {
    const bikeToDelete = bikes.find(bike => bike.id === bikeId);
    if (!bikeToDelete) {
      alert('Bike not found');
      return;
    }
    
    if (bikeToDelete.companyId !== companyId) {
      alert('You can only delete bikes from your own company');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete this bike (${bikeToDelete.regNumber})?`)) {
      await deleteManagedBike(bikeId);
      alert('✅ Bike deleted successfully!');
    }
  };

  const handleAddNewBike = () => {
    setShowAddForm(!showAddForm);
    if (!showAddForm) {
      setEditingBikeId(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Export fleet as CSV in the same column format as the fleet import template
  const handleExportBikes = () => {
    if (bikes.length === 0) {
      alert('No bikes to export');
      return;
    }

    const headers = [
      'Model', 'Plate', 'Type', 'Daily rate', 'Weekly rate', 'Status', 'Condition',
      'Fuel', 'Engine size', 'Year', 'Colour', 'Odometer', 'Notes'
    ];

    const capitalize = (value) =>
      value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : '';

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const rows = bikes.map(bike => [
      bike.model,
      bike.regNumber,
      bike.type || bike.category,
      bike.dailyRate,
      bike.weeklyRate,
      capitalize(bike.status),
      capitalize(bike.condition),
      capitalize(bike.fuelType),
      bike.engineSize,
      bike.year,
      bike.color,
      bike.mileage,
      bike.description
    ].map(escapeCsv).join(','));

    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fleet-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!hasPermission('bikeManagement')) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
          <p className="text-gray-500 text-lg">You don't have permission to access Bike Management.</p>
          <p className="text-gray-600 text-sm mt-2">Contact your company administrator for access.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-20 w-20 text-red-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Error</h3>
          <p className="text-gray-500 text-lg">{error}</p>
          <button 
            onClick={initializeCompanyData}
            className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading bike management data...</p>
          {currentCompany && (
            <p className="text-gray-500 text-sm mt-2">Company: {currentCompany.name}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h3 className="text-xl font-semibold text-white">
            {selectedModel ? (
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setSelectedModel(null)}
                  className="mr-2 p-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300"
                >
                  ←
                </button>
                {selectedModel} Model
              </div>
            ) : (
              "Company Bike Management"
            )}
          </h3>
          {currentCompany && (
            <div className="ml-4 flex items-center gap-2 text-blue-400 text-sm">
              <Building size={16} />
              <span>{currentCompany.name}</span>
              <span className="text-gray-500">•</span>
              <Users size={16} />
              <span>{currentUser?.role}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {saveStatus && (
            <div className={`text-sm rounded-lg px-3 py-1 flex items-center gap-2 ${
              saveStatus === 'saving' ? 'bg-blue-500/20 text-blue-400' :
              saveStatus === 'success' ? 'bg-green-500/20 text-green-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {saveStatus === 'saving' ? 'Saving changes...' :
               saveStatus === 'success' ? 'Changes saved!' :
               'Error saving changes'}
            </div>
          )}
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center gap-2 transition-colors"
            title="Refresh to sync with other sessions"
          >
            🔄 Sync
          </button>
          <button
            onClick={handleExportBikes}
            disabled={bikes.length === 0}
            className="px-3 py-2 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm flex items-center gap-2 transition-colors"
            title="Export fleet as CSV"
          >
            <Download size={16} />
            Export
          </button>
          <button
            onClick={handleAddNewBike}
            className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 px-4 py-2 rounded-lg text-white flex items-center gap-2 transition-colors"
          >
            <Plus size={18} />
            {showAddForm ? 'Cancel' : 'Add New Bike'}
          </button>
        </div>
      </div>

      {bikes.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <div className="text-gray-400 text-sm">Total Bikes</div>
            <div className="text-2xl font-bold text-white">{bikes.length}</div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <div className="text-gray-400 text-sm">Available</div>
            <div className="text-2xl font-bold text-green-400">
              {bikes.filter(bike => bike.status === 'available').length}
            </div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <div className="text-gray-400 text-sm">Rented</div>
            <div className="text-2xl font-bold text-blue-400">
              {bikes.filter(bike => bike.status === 'rented').length}
            </div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <div className="text-gray-400 text-sm">Maintenance</div>
            <div className="text-2xl font-bold text-yellow-400">
              {bikes.filter(bike => bike.status === 'maintenance').length}
            </div>
          </div>
        </div>
      )}

      {bikes.length > 0 && (
        <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <DollarSign className="text-green-400 mt-0.5" size={20} />
            <div>
              <h4 className="text-green-300 font-medium mb-1">Centralized Pricing Active</h4>
              <p className="text-green-200 text-sm">
                The daily rates you set here automatically apply to all invoices, rentals, and calculations across the entire system.
                Any changes will be reflected immediately on all pages.
              </p>
            </div>
          </div>
        </div>
      )}

      {bikes.length === 0 && !showAddForm && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 text-center">
          <div className="text-center">
            <Plus size={48} className="text-blue-400 mx-auto mb-4" />
            <h4 className="text-white font-medium mb-2">No Bikes Added Yet</h4>
            <p className="text-gray-300 text-sm mb-4">
              Get started by adding your first bike to <strong>{currentCompany?.name || 'your company'}</strong>'s fleet.
            </p>
            <button
              onClick={handleAddNewBike}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-6 py-3 rounded-lg text-white flex items-center gap-2 mx-auto transition-colors"
            >
              <Plus size={18} />
              Add Your First Bike
            </button>
          </div>
        </div>
      )}

      {showAddForm ? (
        <BikeDetailsForm
          editingBikeId={editingBikeId}
          bikes={bikes}
          setBikes={setBikes}
          setSaveStatus={setSaveStatus}
          documentTypes={documentTypes}
          setDocumentTypes={setDocumentTypes}
          onSave={handleBikeSaved}
          setShowDocumentTypeManager={setShowDocumentTypeManager}
          setViewingDocument={setViewingDocument}
          saveManagedBike={saveManagedBike}
          currentUser={currentUser}
          currentCompany={currentCompany}
          companyId={companyId}
        />
      ) : (
        <BikeList
          bikes={bikes}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          onEditBike={handleEditBike}
          onDeleteBike={handleDeleteBike}
          setViewingDocument={setViewingDocument}
          sharingStatus={sharingStatus}
          setSharingStatus={setSharingStatus}
          showShareOptions={showShareOptions}
          setShowShareOptions={setShowShareOptions}
          currentUser={currentUser}
          currentCompany={currentCompany}
          companyId={companyId}
        />
      )}

      <DocumentViewer 
        document={viewingDocument} 
        onClose={() => setViewingDocument(null)} 
      />
      
      <DocumentTypeManager
        show={showDocumentTypeManager}
        onClose={() => setShowDocumentTypeManager(false)}
        documentTypes={documentTypes}
        setDocumentTypes={setDocumentTypes}
      />
      
      {Object.values(sharingStatus).some(status => status === 'success') && (
        <div className="fixed bottom-4 right-4 bg-green-500/95 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <div className="bg-white/20 p-1 rounded-full">
            <Check size={16} />
          </div>
          <div>
            <div className="font-medium">Copied to clipboard</div>
            <div className="text-xs text-green-100">Bike details have been copied</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BikeManagement;